import React from 'react';
import {
  Link,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type { SupplyPoint, Route } from '@/types';

const parseDateInputAsLocalDate = (value: string): Date => {
  const rawValue = String(value ?? '').trim();
  if (!rawValue) {
    return new Date('');
  }

  const localDateMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (localDateMatch) {
    const year = Number(localDateMatch[1]);
    const month = Number(localDateMatch[2]);
    const day = Number(localDateMatch[3]);
    return new Date(year, month - 1, day);
  }

  return new Date(rawValue);
};

const parseTimeInput = (value: string): string => {
  const rawValue = String(value ?? '').trim();
  if (!rawValue) {
    return '';
  }

  const normalizedValue = rawValue.toUpperCase();
  const amPmMatch = normalizedValue.match(/^(\d{1,2}):(\d{2})(?:\s)?([AP]M)$/);
  if (amPmMatch) {
    const hours = Number(amPmMatch[1]);
    const minutes = Number(amPmMatch[2]);
    const period = amPmMatch[3];
    if (hours >= 1 && hours <= 12 && minutes >= 0 && minutes <= 59) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
  }

  const twentyFourHourMatch = normalizedValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (twentyFourHourMatch) {
    const parsedHours = Number(twentyFourHourMatch[1]);
    const parsedMinutes = Number(twentyFourHourMatch[2]);
    if (parsedHours >= 0 && parsedHours <= 23 && parsedMinutes >= 0 && parsedMinutes <= 59) {
      const period = parsedHours >= 12 ? 'PM' : 'AM';
      const hours = parsedHours % 12 || 12;
      return `${hours.toString().padStart(2, '0')}:${parsedMinutes.toString().padStart(2, '0')} ${period}`;
    }
  }

  return rawValue;
};

const requestedTimeOptions = Array.from({ length: 24 * 4 }, (_, index) => {
  const totalMinutes = index * 15;
  const hours24 = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
});

interface OrderDetailsCardProps {
  supplyPointsLoading: boolean;
  routesLoading: boolean;
  supplyPointSelected: boolean;
  shouldValidateTransportationDates?: boolean;
  routeDetailsError?: boolean;
  earliestRequestedDate?: string;
  requestedDateMinInput?: string;
  onSelectSupplyPoint: (supplyPoint: SupplyPoint) => void;
  onDeleteSupplyPoint: () => void;
  onSelectRoute: (route: Route) => void;
  onOpenSupplyPointDialog: () => void;
  onOpenRouteDialog: () => void;
  onRetryRouteDetails?: () => void;
  onSetEarliestRequestedDate?: () => void;
}

export const OrderDetailsCard: React.FC<OrderDetailsCardProps> = ({
  supplyPointsLoading,
  routesLoading,
  supplyPointSelected,
  shouldValidateTransportationDates = false,
  routeDetailsError = false,
  earliestRequestedDate,
  requestedDateMinInput,
  onSelectSupplyPoint,
  onDeleteSupplyPoint,
  onSelectRoute,
  onOpenSupplyPointDialog,
  onOpenRouteDialog,
  onRetryRouteDetails,
  onSetEarliestRequestedDate,
}) => {
  const { control, formState, setValue, trigger } = useFormContext();
  const isPickup = useWatch({ control, name: 'isPickup' });
  const lineFieldSx = {
    mb: 2,
    '& .MuiInputBase-root:before': {
      borderBottomWidth: 1,
    },
  };

  const getRequestedTimeLabel = () => {
    return isPickup === 'pickup' ? 'Requested Pick-up Time (Optional)' : 'Requested Delivery Time (Optional)';
  };

  const getDeliveryOrPickupLabel = () => {
    return isPickup === 'pickup' ? 'Pick-up' : 'Delivery';
  };

  return (
    <Card sx={{ mb: 2 }} variant="outlined">
      <CardHeader title="Order Details" />
      <CardContent sx={{ pt: 3, px: 3, pb: 3, minWidth: 0, maxWidth: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 4.5,
            maxWidth: '100%',
            flexWrap: 'nowrap',
          }}
        >
          <Box
            sx={{
              flex: '0 1 calc(25% - 27px)',
              minWidth: 0,
            }}
          >
            <Box sx={{ pt: 3, pb: 2 }}>
              <Controller
                name="isPickup"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    row
                    {...field}
                    value={field.value === 'pickup' ? 'true' : 'false'}
                    onChange={(e) => {
                      const nextValue = e.target.value === 'true' ? 'pickup' : 'delivery';
                      field.onChange(nextValue);
                      setValue('isPickup', nextValue, {
                        shouldDirty: true,
                        shouldValidate: true,
                        shouldTouch: true,
                      });
                    }}
                  >
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="Delivery"
                      id="qa-delivery-rdo"
                    />
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Pick-up"
                      id="qa-pickUp-rdo"
                    />
                  </RadioGroup>
                )}
              />
            </Box>

            {isPickup !== 'pickup' ? (
              <Controller
                name="requestedDeliveryDate"
                control={control}
                rules={{ required: 'Requested Delivery Date is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    label="Requested Delivery Date"
                    id="qa-requestedDeliveryDate-ipt"
                    inputProps={{ min: requestedDateMinInput }}
                    onChange={(event) => {
                      field.onChange(event);
                      void trigger('requestedDeliveryDate');
                    }}
                    error={!!formState.errors.requestedDeliveryDate}
                    helperText={typeof formState.errors.requestedDeliveryDate?.message === 'string' ? formState.errors.requestedDeliveryDate.message : undefined}
                    sx={lineFieldSx}
                  />
                )}
              />
            ) : (
              <Controller
                name="requestedPickupDate"
                control={control}
                rules={{ required: 'Requested Pick-up Date is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    label="Requested Pick-up Date"
                    id="qa-requestedPickUpDate-ipt"
                    inputProps={{ min: requestedDateMinInput }}
                    onChange={(event) => {
                      field.onChange(event);
                      void trigger('requestedPickupDate');
                    }}
                    error={!!formState.errors.requestedPickupDate}
                    helperText={typeof formState.errors.requestedPickupDate?.message === 'string' ? formState.errors.requestedPickupDate.message : undefined}
                    sx={lineFieldSx}
                  />
                )}
              />
            )}

            <Controller
              name="requestedDeliveryTime"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="standard"
                  label={getRequestedTimeLabel()}
                  id="qa-requestedDeliveryTime-ipt"
                  value={parseTimeInput(String(field.value ?? ''))}
                  onChange={(event) => {
                    field.onChange(String(event.target.value ?? '').trim());
                    void trigger('requestedDeliveryTime');
                  }}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    IconComponent: ArrowDropDownIcon,
                    renderValue: (selected) => String(selected ?? '').trim() || 'hh:mm AM/PM',
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 48 * 6,
                        },
                      },
                    },
                  }}
                  sx={lineFieldSx}
                >
                  <MenuItem value="">
                    <em>hh:mm AM/PM</em>
                  </MenuItem>
                  {requestedTimeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="plannedLoadDate"
              control={control}
              render={({ field }) => {
                const rawDateValue = String(field.value ?? '').trim();
                const parsedDate = parseDateInputAsLocalDate(rawDateValue);
                const plannedDate = new Date(parsedDate);
                plannedDate.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const hasDatePassed =
                  shouldValidateTransportationDates
                  && rawDateValue.length > 0
                  && !Number.isNaN(plannedDate.getTime())
                  && plannedDate < today;

                return (
                  <TextField
                    {...field}
                    type="date"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    label="Planned Load Date"
                    id="qa-plannedLoadDate-ipt"
                    InputProps={{ readOnly: true }}
                    error={!!hasDatePassed}
                    sx={lineFieldSx}
                  />
                );
              }}
            />

            <Controller
              name="transportationPlannedDate"
              control={control}
              render={({ field }) => {
                const rawDateValue = String(field.value ?? '').trim();
                const parsedDate = parseDateInputAsLocalDate(rawDateValue);
                const plannedDate = new Date(parsedDate);
                plannedDate.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const hasDatePassed =
                  shouldValidateTransportationDates
                  && rawDateValue.length > 0
                  && !Number.isNaN(plannedDate.getTime())
                  && plannedDate < today;
                return (
                  <TextField
                    {...field}
                    type="date"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    label="Transportation Planning Date"
                    id="qa-transportationPlannedDate-ipt"
                    InputProps={{ readOnly: true }}
                    error={!!hasDatePassed}
                    helperText={
                      hasDatePassed
                        ? (
                          <span id="qa-transportationPlannedDate-errormsg">
                            Date has passed,
                            {' '}
                            <Link
                              id="qa-updateroute-link"
                              component="button"
                              type="button"
                              underline="none"
                              onClick={onOpenRouteDialog}
                            >
                              update route
                            </Link>
                            {' '}or{' '}
                            <Link
                              id="qa-updaterequesteddate-link"
                              component="button"
                              type="button"
                              underline="none"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onSetEarliestRequestedDate?.();
                              }}
                            >
                              {`${getDeliveryOrPickupLabel().toLowerCase()} date${earliestRequestedDate ? ` to ${earliestRequestedDate}` : ''}`}
                            </Link>
                          </span>
                        )
                        : undefined
                    }
                    sx={lineFieldSx}
                  />
                );
              }}
            />
          </Box>

          <Box
            sx={{
              flex: '0 1 calc(25% - 27px)',
              minWidth: 0,
            }}
          >
            {supplyPointSelected && (
              <Typography
                component="h3"
                sx={{ mt: 3, mb: 1.5, fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}
              >
                Supply Point
              </Typography>
            )}

            {!supplyPointSelected ? (
              <Button
                variant="contained"
                fullWidth
                onClick={onOpenSupplyPointDialog}
                id="qa-selectSupplyPoint-btn"
                sx={{ mt: 3, mb: 1 }}
              >
                SELECT SUPPLY POINT
              </Button>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Controller
                    name="supplyPointCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="standard"
                        fullWidth
                        size="small"
                        label="Supply Point Code"
                        id="qa-plantNumber-ipt"
                        InputProps={{ readOnly: true }}
                        error={!!formState.errors.supplyPointCode}
                        helperText={typeof formState.errors.supplyPointCode?.message === 'string' ? formState.errors.supplyPointCode.message : undefined}
                        sx={lineFieldSx}
                      />
                    )}
                  />
                  <IconButton
                    size="small"
                    onClick={onOpenSupplyPointDialog}
                    id="qa-editSupplyPoint-btn"
                    sx={{ color: 'primary.main' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={onDeleteSupplyPoint}
                    id="qa-deleteSupplyPoint-btn"
                    sx={{ color: 'text.primary' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Controller
                  name="supplyPointName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                        variant="standard"
                      fullWidth
                      size="small"
                      label="Supply Point Name"
                      id="qa-supplyPointName-ipt"
                      InputProps={{ readOnly: true }}
                        sx={lineFieldSx}
                    />
                  )}
                />

                <Controller
                  name="supplyPointAddress1"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                        variant="standard"
                      fullWidth
                      size="small"
                      label="Address line 1"
                      id="qa-addressLine1-ipt"
                      InputProps={{ readOnly: true }}
                        sx={lineFieldSx}
                    />
                  )}
                />

                <Controller
                  name="supplyPointAddress2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                        variant="standard"
                      fullWidth
                      size="small"
                      label="Address line 2"
                      id="qa-addressLine2-ipt"
                      InputProps={{ readOnly: true }}
                        sx={lineFieldSx}
                    />
                  )}
                />

                <Controller
                  name="supplyPointCity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                        variant="standard"
                      fullWidth
                      size="small"
                      label="City"
                      id="qa-city-ipt"
                      InputProps={{ readOnly: true }}
                        sx={lineFieldSx}
                    />
                  )}
                />

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Controller
                    name="supplyPointState"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="standard"
                        fullWidth
                        size="small"
                        label="State/Prov"
                        id="qa-state-ipt"
                        InputProps={{ readOnly: true }}
                        sx={lineFieldSx}
                      />
                    )}
                  />
                  <Controller
                    name="supplyPointCountry"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="standard"
                        fullWidth
                        size="small"
                        label="Country"
                        id="qa-country-ipt"
                        InputProps={{ readOnly: true }}
                        sx={lineFieldSx}
                      />
                    )}
                  />
                  <Controller
                    name="supplyPointZipCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="standard"
                        fullWidth
                        size="small"
                        label="Postal Code"
                        id="qa-postalCode-ipt"
                        InputProps={{ readOnly: true }}
                        sx={lineFieldSx}
                      />
                    )}
                  />
                </Box>
              </>
            )}
          </Box>

          <Box
            sx={{
              flex: '0 1 calc(25% - 27px)',
              minWidth: 0,
            }}
          >
            <Typography
              component="h3"
              sx={{ mt: 3, mb: 1.5, fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}
            >
              Routing Details
            </Typography>

            {routesLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                <CircularProgress size={50} className="loading-spinner" sx={{ color: '#0c479d', m: 0 }} />
                <Typography sx={{ mt: 2, color: 'rgba(0, 0, 0, 0.6)', fontSize: '1rem', letterSpacing: '0.5px', lineHeight: '28px' }}>
                  Selecting route based on Ship-to, Supply Point, and Products
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Controller
                    name="routeCode"
                    control={control}
                    rules={{ required: 'Route code is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        variant="standard"
                        fullWidth
                        size="small"
                        label="Route Code"
                        id="qa-routeCode-ipt"
                        InputProps={{ readOnly: true }}
                        error={!!formState.errors.routeCode}
                        helperText={typeof formState.errors.routeCode?.message === 'string' ? formState.errors.routeCode.message : undefined}
                        sx={lineFieldSx}
                      />
                    )}
                  />
                  {supplyPointSelected && (
                    <IconButton
                      size="small"
                      onClick={onOpenRouteDialog}
                      id="qa-editRoute-btn"
                      sx={{ color: 'primary.main' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Controller
                  name="routeDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                        variant="standard"
                      fullWidth
                      size="small"
                      label="Route Name"
                      id="qa-routeDescription-ipt"
                      InputProps={{ readOnly: true }}
                        sx={lineFieldSx}
                    />
                  )}
                />

                <Controller
                  name="transitDays"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                        variant="standard"
                      fullWidth
                      size="small"
                      label="Transit Days"
                      id="qa-transitDays-ipt"
                      type="text"
                      InputProps={{ readOnly: true }}
                      sx={lineFieldSx}
                    />
                  )}
                />

                {routeDetailsError && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      id="qa-routedetails-errormsg"
                      variant="caption"
                      color="error"
                    >
                      Route selection failed.{' '}
                      <Link
                        id="qa-getroutedetails-link"
                        component="button"
                        type="button"
                        underline="none"
                        color="error"
                        variant="caption"
                        onClick={onRetryRouteDetails}
                      >
                        Try Again
                      </Link>
                    </Typography>
                  </Box>
                )}

              </>
            )}
          </Box>

          <Box
            sx={{
              flex: '0 1 calc(25% - 27px)',
              minWidth: 0,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
